/**
 * Amortización método flat:
 * el interés por cuota se calcula sobre el capital inicial
 * y se mantiene constante en todas las cuotas.
 *
 * En esta versión:
 * - SIEMPRE se usa el BALANCE PENDIENTE del archivo
 * - desde ahí se calcula cuánto se ha pagado
 * - luego se distribuye cuota por cuota
 * - prioridad en cuota parcial: interés primero, luego capital
 */

function parseNumero(val) {
  if (val == null || val === "") return null;
  let t = String(val).trim().replace(/\s/g, "");
  if (t === "") return null;

  const hasComma = t.includes(",");
  const hasDot = t.includes(".");

  if (/^\d{1,3}(\.\d{3})+,\d{1,4}$/.test(t)) {
    t = t.replace(/\./g, "").replace(",", ".");
  } else if (hasComma && !hasDot && /^\d+,\d{1,2}$/.test(t)) {
    t = t.replace(",", ".");
  } else {
    t = t.replace(/,/g, "");
  }

  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function getCampo(row, ...nombres) {
  const keys = Object.keys(row).filter((k) => k !== "id");
  for (const nombre of nombres) {
    const want = nombre.trim().toLowerCase();
    const found = keys.find((k) => k.trim().toLowerCase() === want);
    if (found !== undefined) return row[found];
  }
  return undefined;
}

function cedulaConFormatoValido(value) {
  return /^\d{3}-\d{7}-\d{1}$/.test(String(value ?? "").trim());
}

function frecuenciaRawValida(value) {
  return ["Mensual", "Dias", "Quincenas", "Semanas"].includes(String(value ?? "").trim());
}

export function validarPrestamoRow(row) {
  const errors = [];
  const cedula = String(getCampo(row, "cedula", "Cedula", "Cédula") ?? "").trim();
  const capital = parseNumero(getCampo(row, "Capital", "capital"));
  const tasa = parseNumero(getCampo(row, "tasa", "Tasa"));
  const plazo = parseNumero(getCampo(row, "plazo", "Plazo"));
  const frecuenciaRaw = String(getCampo(row, "frecuencia", "Frecuencia") ?? "").trim();
  const frecuencia = parseFrecuencia(frecuenciaRaw);
  const fechaInicio = parseFechaLoose(getCampo(row, "fecha de inicio", "fecha de inicio "));
  const balancePendiente = parseNumero(
    getCampo(row, "balance pendiente", "balancependiente", "saldo pendiente", "saldo_pendiente")
  );

  if (capital == null || capital <= 0) {
    errors.push({ field: "capital", message: "Capital debe ser un número mayor que cero." });
  }

  if (tasa == null || tasa < 0) {
    errors.push({ field: "tasa", message: "Tasa debe ser un número mayor o igual a cero." });
  }

  const plazoEntero = plazo != null && plazo > 0 ? Math.max(1, Math.round(plazo)) : null;
  if (plazoEntero == null) {
    errors.push({ field: "plazo", message: "Plazo debe ser un número positivo (número de cuotas)." });
  }

  if (!frecuenciaRawValida(frecuenciaRaw) || frecuencia == null || frecuencia <= 0) {
    errors.push({
      field: "frecuencia",
      message: "Frecuencia no válida. Debe ser exactamente una de estas: Mensual, Dias, Quincenas, Semanas.",
    });
  }

  if (!cedulaConFormatoValido(cedula)) {
    errors.push({
      field: "cedula",
      message: "Cédula no válida. Debe tener formato 000-0000000-0 (ejemplo: 402-0913689-0).",
    });
  }

  if (!fechaInicio) {
    errors.push({ field: "fecha de inicio", message: "Fecha de inicio no válida o vacía." });
  }

  if (balancePendiente == null || balancePendiente < 0) {
    errors.push({ field: "balance pendiente", message: "Balance pendiente no válido o vacío." });
  }

  if (errors.length > 0) {
    return { ok: false, normalizedData: null, errors };
  }

  return {
    ok: true,
    normalizedData: {
      cedula,
      capital,
      tasa,
      plazo: plazoEntero,
      frecuenciaRaw,
      frecuencia,
      fechaInicio,
      balancePendiente,
    },
    errors: [],
  };
}

export function parseFrecuencia(val) {
  if (val == null || val === "") return null;
  const s = String(val).trim();
  const map = {
    Mensual: 12,
    Dias: 365,
    Quincenas: 24,
    Semanas: 52,
  };
  if (map[s] != null) return map[s];
  return null;
}

function makeLocalDate(year, month, day) {
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > 31
  ) {
    return null;
  }
  const date = new Date(y, m - 1, d);
  // Rechaza fechas imposibles tipo 31/02 (JS las "rebalancea" silenciosamente).
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export function parseFechaLoose(val) {
  if (val == null || val === "") return null;

  if (val instanceof Date) {
    if (Number.isNaN(val.getTime())) return null;
    return new Date(val.getFullYear(), val.getMonth(), val.getDate());
  }

  if (typeof val === "number" && Number.isFinite(val)) {
    // Excel epoch (1899-12-30) en UTC para evitar que DST/zona horaria
    // muevan el día al convertir.
    const ms = Date.UTC(1899, 11, 30) + Math.floor(val) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  let s = String(val).trim();
  if (s === "") return null;

  if (s.includes("T")) s = s.split("T")[0];
  if (s.includes(" ")) s = s.split(" ")[0];

  // ISO: YYYY-MM-DD o YYYY/MM/DD
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return makeLocalDate(m[1], m[2], m[3]);

  // DD/MM/YYYY, D/M/YY, DD-MM-YYYY, DD.MM.YYYY
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if (m) {
    let year = m[3];
    if (year.length === 2) {
      const yi = parseInt(year, 10);
      year = String(yi >= 70 ? 1900 + yi : 2000 + yi);
    }
    return makeLocalDate(year, m[2], m[1]);
  }

  // Serial de Excel pasado como string ("45658" o "45658.5")
  if (/^\d+(\.\d+)?$/.test(s)) {
    return parseFechaLoose(parseFloat(s));
  }

  return null;
}

function addMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function sumarSegunFrecuencia(fechaInicio, periodo, frecuencia) {
  if (frecuencia === 12) return addMonths(fechaInicio, periodo);
  if (frecuencia === 24) return addDays(fechaInicio, periodo * 15);
  if (frecuencia === 52) return addDays(fechaInicio, periodo * 7);
  if (frecuencia === 6) return addMonths(fechaInicio, periodo * 2);
  if (frecuencia === 4) return addMonths(fechaInicio, periodo * 3);
  if (frecuencia === 2) return addMonths(fechaInicio, periodo * 6);
  if (frecuencia === 1) return addMonths(fechaInicio, periodo * 12);

  const diasPorPeriodo = Math.round(365 / frecuencia);
  return addDays(fechaInicio, periodo * diasPorPeriodo);
}

/**
 * Calcula capital pagado e interés pagado usando SIEMPRE el balance pendiente.
 * Regla:
 * - totalPagado = totalPrestamo - balancePendiente
 * - se aplica cuota por cuota
 * - en cuota parcial: interés primero, luego capital
 */
function calcularPagadoDesdeBalancePendiente({
  capital,
  plazo,
  balancePendiente,
  capitalPorCuotaBase,
  interesPorCuota,
}) {
  const interesTotal = Number((interesPorCuota * plazo).toFixed(2));
  const totalPrestamo = Number((capital + interesTotal).toFixed(2));

  const balancePendienteSeguro = Number(
    Math.max(0, Math.min(balancePendiente ?? totalPrestamo, totalPrestamo)).toFixed(2)
  );

  let totalPagado = Number(
    (totalPrestamo - balancePendienteSeguro).toFixed(2)
  );

  let capitalPagado = 0;
  let interesPagado = 0;
  let capitalProgramadoAcumulado = 0;

  for (let k = 1; k <= plazo; k += 1) {
    let capitalProgramado;
    if (k < plazo) {
      capitalProgramado = Number(capitalPorCuotaBase.toFixed(2));
    } else {
      capitalProgramado = Number(
        (capital - capitalProgramadoAcumulado).toFixed(2)
      );
    }

    capitalProgramadoAcumulado = Number(
      (capitalProgramadoAcumulado + capitalProgramado).toFixed(2)
    );

    const interesProgramado = Number(interesPorCuota.toFixed(2));
    const cuotaProgramada = Number(
      (capitalProgramado + interesProgramado).toFixed(2)
    );

    if (totalPagado <= 0) break;

    if (totalPagado >= cuotaProgramada) {
      interesPagado = Number((interesPagado + interesProgramado).toFixed(2));
      capitalPagado = Number((capitalPagado + capitalProgramado).toFixed(2));
      totalPagado = Number((totalPagado - cuotaProgramada).toFixed(2));
    } else {
      // cuota parcial: primero interés
      const interesAplicado = Number(
        Math.min(totalPagado, interesProgramado).toFixed(2)
      );
      interesPagado = Number((interesPagado + interesAplicado).toFixed(2));
      totalPagado = Number((totalPagado - interesAplicado).toFixed(2));

      if (totalPagado > 0) {
        const capitalAplicado = Number(
          Math.min(totalPagado, capitalProgramado).toFixed(2)
        );
        capitalPagado = Number((capitalPagado + capitalAplicado).toFixed(2));
        totalPagado = Number((totalPagado - capitalAplicado).toFixed(2));
      }

      break;
    }
  }

  return {
    capitalPagadoImporte: Number(capitalPagado.toFixed(2)),
    interesPagadoImporte: Number(interesPagado.toFixed(2)),
    interesTotal,
    totalPrestamo,
    balancePendiente: balancePendienteSeguro,
  };
}

export function extraerParametrosPrestamo(row) {
  const validation = validarPrestamoRow(row);
  if (!validation.ok) {
    return { ok: false, error: validation.errors[0]?.message || "Datos del préstamo no válidos." };
  }
  const { capital, tasa, plazo: plazoEntero, frecuenciaRaw, frecuencia, fechaInicio, balancePendiente } =
    validation.normalizedData;

  const capitalPorCuotaBase = capital / plazoEntero;

  // Se asume tasa flat por cuota/período
  var tasaFinal = tasa;
  if(frecuenciaRaw == "Dias") {
    tasaFinal = tasa / 30;
  } else if(frecuenciaRaw == "Quincenas") {
    tasaFinal = tasa / 2;
  } 
  else if(frecuenciaRaw == "Semanas") {
    tasaFinal = tasa / 4;
  }
  const interesPorCuota = capital * (tasaFinal / 100);
  const cuotaBase = capitalPorCuotaBase + interesPorCuota;

  const calculado = calcularPagadoDesdeBalancePendiente({
    capital,
    plazo: plazoEntero,
    balancePendiente,
    capitalPorCuotaBase,
    interesPorCuota,
  });

  return {
    ok: true,
    params: {
      capital,
      tasa,
      plazo: plazoEntero,
      frecuencia,
      fechaInicio,
      cuotaBase,
      capitalPorCuotaBase,
      interesPorCuota,
      interesTotal: calculado.interesTotal,
      capitalPagadoImporte: calculado.capitalPagadoImporte,
      interesPagadoImporte: calculado.interesPagadoImporte,
      balancePendienteOriginal: calculado.balancePendiente,
      totalPrestamo: calculado.totalPrestamo,
    },
  };
}

export function generarTablaAmortizacionFlat(row) {
  const ext = extraerParametrosPrestamo(row);
  if (!ext.ok) return ext;

  const {
    capital,
    plazo,
    frecuencia,
    fechaInicio,
    cuotaBase,
    capitalPorCuotaBase,
    interesPorCuota,
    interesTotal,
    capitalPagadoImporte,
    interesPagadoImporte,
    balancePendienteOriginal,
    totalPrestamo,
  } = ext.params;

  const filas = [];

  let capitalRestantePorAplicar = Math.min(capitalPagadoImporte, capital);
  let interesRestantePorAplicar = Math.min(interesPagadoImporte, interesTotal);

  let capitalProgramadoAcumulado = 0;
  let capitalAplicadoAcumulado = 0;

  for (let k = 1; k <= plazo; k += 1) {
    const fecha = sumarSegunFrecuencia(fechaInicio, k, frecuencia);

    let capitalProgramado;
    if (k < plazo) {
      capitalProgramado = Number(capitalPorCuotaBase.toFixed(2));
    } else {
      capitalProgramado = Number(
        (capital - capitalProgramadoAcumulado).toFixed(2)
      );
    }

    capitalProgramadoAcumulado = Number(
      (capitalProgramadoAcumulado + capitalProgramado).toFixed(2)
    );

    const interesProgramado = Number(interesPorCuota.toFixed(2));
    const cuotaProgramada = Number(
      (capitalProgramado + interesProgramado).toFixed(2)
    );

    const capitalAplicado = Number(
      Math.min(capitalRestantePorAplicar, capitalProgramado).toFixed(2)
    );
    capitalRestantePorAplicar = Number(
      (capitalRestantePorAplicar - capitalAplicado).toFixed(2)
    );

    const interesAplicado = Number(
      Math.min(interesRestantePorAplicar, interesProgramado).toFixed(2)
    );
    interesRestantePorAplicar = Number(
      (interesRestantePorAplicar - interesAplicado).toFixed(2)
    );

    const capitalPendiente = Number(
      (capitalProgramado - capitalAplicado).toFixed(2)
    );
    const interesPendiente = Number(
      (interesProgramado - interesAplicado).toFixed(2)
    );
    const totalPagado = Number(
      (capitalAplicado + interesAplicado).toFixed(2)
    );

    let estado = "pendiente";
    if (capitalPendiente === 0 && interesPendiente === 0) {
      estado = "pagada";
    } else if (capitalAplicado > 0 || interesAplicado > 0) {
      estado = "parcial";
    }

    capitalAplicadoAcumulado = Number(
      (capitalAplicadoAcumulado + capitalAplicado).toFixed(2)
    );

    const saldo = Number(
      Math.max(0, capital - capitalAplicadoAcumulado).toFixed(2)
    );

    filas.push({
      periodo: k,
      fecha,
      cuota: cuotaProgramada,
      capital: capitalProgramado,
      interes: interesProgramado,
      capitalPagado: capitalAplicado,
      interesPagado: interesAplicado,
      totalPagado,
      capitalPendiente,
      interesPendiente,
      saldo,
      estado,
    });
  }

  const cuotasPagadasCompletas = filas.filter((f) => f.estado === "pagada").length;
  const cuotasParciales = filas.filter((f) => f.estado === "parcial").length;
  const cuotasPendientes = filas.filter((f) => f.estado === "pendiente").length;

  const capitalPagadoReal = Number(
    filas.reduce((acc, f) => acc + f.capitalPagado, 0).toFixed(2)
  );
  const interesPagadoReal = Number(
    filas.reduce((acc, f) => acc + f.interesPagado, 0).toFixed(2)
  );

  const capitalPendienteTotal = Number(
    Math.max(0, capital - capitalPagadoReal).toFixed(2)
  );
  const interesPendienteTotal = Number(
    Math.max(0, interesTotal - interesPagadoReal).toFixed(2)
  );
  const balancePendiente = Number(
    (capitalPendienteTotal + interesPendienteTotal).toFixed(2)
  );

  return {
    ok: true,
    filas,
    resumen: {
      capitalInicial: Number(capital.toFixed(2)),
      interesTotal: Number(interesTotal.toFixed(2)),
      totalPrestamo: Number(totalPrestamo.toFixed(2)),
      cuotaFija: Number(cuotaBase.toFixed(2)),
      plazo,
      frecuencia,

      capitalPagado: capitalPagadoReal,
      interesPagado: interesPagadoReal,

      capitalPendiente: capitalPendienteTotal,
      interesPendiente: interesPendienteTotal,
      balancePendiente,
      balancePendienteOriginal,

      cuotasPagadasCompletas,
      cuotasParciales,
      cuotasPendientes,

      sobranteCapitalNoAplicado: Number(
        Math.max(0, capitalRestantePorAplicar).toFixed(2)
      ),
      sobranteInteresNoAplicado: Number(
        Math.max(0, interesRestantePorAplicar).toFixed(2)
      ),
    },
  };
}

export function formatearMoneda(val) {
  if (!Number.isFinite(val)) return "—";
  return val.toLocaleString("es-HN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatearFecha(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-HN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}